import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Search, Package, Clock, CheckCircle, Truck, 
  XCircle, Factory, Trash2, FileText, AlertTriangle,
  Building2, RefreshCw, Eye, X, Check, Download, Upload,
  FileSpreadsheet, AlertCircle, History, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';
import UploadProgress from '../components/UploadProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending_approval: { label: 'Pending Approval', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  changes_requested: { label: 'Changes Requested', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  in_production: { label: 'In Production', color: 'bg-purple-100 text-purple-700', icon: Factory },
  shipped: { label: 'Shipped', color: 'bg-cyan-100 text-cyan-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700', icon: XCircle }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const BulkOrders = () => {
  const { getAuthHeaders, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  
  // View order dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  
  // Reject dialog
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Request changes dialog
  const [showChangesDialog, setShowChangesDialog] = useState(false);
  const [changingOrder, setChangingOrder] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  
  // Re-upload dialog
  const [showReuploadDialog, setShowReuploadDialog] = useState(false);
  const [reuploadingOrder, setReuploadingOrder] = useState(null);

  const isFactoryAdmin = user?.role === 'factory_admin';
  const isAdmin = user?.role === 'admin';
  const isDepot = user?.role === 'depot';
  const canManageOrders = isFactoryAdmin || isAdmin;

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/bulk-orders`, { headers: getAuthHeaders() });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching bulk orders:', error);
      setError('Unable to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [getAuthHeaders]);

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    const matchesSearch = search.trim() === '' || 
      order.depot_name?.toLowerCase().includes(search.toLowerCase()) ||
      order.items?.some(item => item.item_name.toLowerCase().includes(search.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  // Download template
  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API}/bulk-orders/template`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `bulk_order_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded! Fill in the Order Qty column and upload.');
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Upload order
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/bulk-orders/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      toast.success(`Order created with ${response.data.order.total_items} items!`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload order');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = ''; // Reset file input
    }
  };

  // Re-upload order
  const handleReupload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !reuploadingOrder) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/bulk-orders/${reuploadingOrder.id}/reupload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      toast.success('Order updated and resubmitted for approval!');
      setShowReuploadDialog(false);
      setReuploadingOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-upload order');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };

  // Approve order
  const approveOrder = async (orderId) => {
    try {
      const response = await axios.put(`${API}/bulk-orders/${orderId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success(`Order approved! ${response.data.reorders_created?.length || 0} reorders created.`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve order');
    }
  };

  // Request changes
  const requestChanges = async () => {
    if (!changingOrder || !changeReason.trim()) return;
    
    try {
      await axios.put(
        `${API}/bulk-orders/${changingOrder.id}/request-changes`,
        { status: 'changes_requested', change_request_reason: changeReason },
        { headers: getAuthHeaders() }
      );
      toast.success('Changes requested. Depot will be notified to re-upload.');
      setShowChangesDialog(false);
      setChangingOrder(null);
      setChangeReason('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request changes');
    }
  };

  // Reject order
  const rejectOrder = async () => {
    if (!rejectingOrder) return;
    
    try {
      await axios.put(
        `${API}/bulk-orders/${rejectingOrder.id}/reject`,
        { status: 'rejected', rejection_reason: rejectionReason },
        { headers: getAuthHeaders() }
      );
      toast.success('Order rejected');
      setShowRejectDialog(false);
      setRejectingOrder(null);
      setRejectionReason('');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject order');
    }
  };

  // Cancel order
  const cancelOrder = async (orderId) => {
    try {
      await axios.delete(`${API}/bulk-orders/${orderId}`, { headers: getAuthHeaders() });
      toast.success('Order cancelled');
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const pendingCount = orders.filter(o => o.status === 'pending_approval').length;
  const changesRequestedCount = orders.filter(o => o.status === 'changes_requested').length;

  if (loading) {
    return <LoadingOverlay message="Loading orders..." />;
  }

  if (error) {
    return <ErrorState title="Failed to load orders" message={error} onRetry={fetchOrders} />;
  }

  return (
    <div className="space-y-6" data-testid="bulk-orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bulk Orders
          </h1>
          <p className="text-slate-500 mt-1">
            {isDepot ? 'Create orders using Excel template' : 'Review and approve depot orders'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchOrders} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Approval</p>
                <p className="text-2xl font-bold text-yellow-600">{pendingCount}</p>
              </div>
              <div className="bg-yellow-100 p-2 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Changes Requested</p>
                <p className="text-2xl font-bold text-orange-600">{changesRequestedCount}</p>
              </div>
              <div className="bg-orange-100 p-2 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {orders.filter(o => o.status === 'approved').length}
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Order Section (Depot Only) */}
      {isDepot && (
        <Card className="border-2 border-dashed border-blue-200 bg-blue-50/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Create New Order</h3>
                <div className="flex items-center gap-2 text-slate-600 mb-4">
                  <span className="flex items-center gap-1 text-sm">
                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">1</span>
                    Download Template
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <span className="flex items-center gap-1 text-sm">
                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">2</span>
                    Fill Order Qty
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <span className="flex items-center gap-1 text-sm">
                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">3</span>
                    Upload File
                  </span>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={downloadTemplate} 
                  variant="outline"
                  disabled={downloadingTemplate}
                  className="border-blue-300 hover:bg-blue-100"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                </Button>
                {!uploading && (
                  <label>
                    <Button 
                      asChild
                      className="bg-blue-600 hover:bg-blue-700 cursor-pointer"
                      disabled={uploading}
                    >
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Order
                      </span>
                    </Button>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
              {uploading && (
                <div className="mt-3">
                  <UploadProgress progress={uploadProgress} message="Uploading bulk order..." />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search orders by depot or item..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <config.icon className="w-3 h-3" />
                      {config.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No orders found</p>
              <p className="text-slate-400 text-sm">
                {isDepot ? 'Download the template and upload your first order' : 'No depot orders to review'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredOrders.map(order => {
            const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
            const needsReupload = order.status === 'changes_requested' && isDepot;
            
            return (
              <Card key={order.id} className={`card-hover ${needsReupload ? 'border-orange-300 bg-orange-50/30' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <Badge className="bg-slate-100 text-slate-700 border-0">
                          <Building2 className="w-3 h-3 mr-1" />
                          {order.depot_name}
                        </Badge>
                        <Badge className={`${STATUS_CONFIG[order.status]?.color} border-0`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {STATUS_CONFIG[order.status]?.label}
                        </Badge>
                        {order.reorders_created && (
                          <Badge className="bg-green-100 text-green-700 border-0">
                            {order.reorders_created} Reorders Created
                          </Badge>
                        )}
                      </div>
                      
                      <div className="text-sm text-slate-500 space-y-1">
                        <p><span className="font-medium">Created:</span> {formatDate(order.created_at)} by {order.created_by_name}</p>
                        {order.approved_at && (
                          <p className="text-green-600"><span className="font-medium">Approved:</span> {formatDate(order.approved_at)} by {order.approved_by_name}</p>
                        )}
                        {order.change_request_reason && (
                          <p className="text-orange-600 bg-orange-50 p-2 rounded mt-2">
                            <span className="font-medium">Changes Requested:</span> {order.change_request_reason}
                          </p>
                        )}
                        {order.rejection_reason && (
                          <p className="text-red-600 bg-red-50 p-2 rounded mt-2">
                            <span className="font-medium">Rejection reason:</span> {order.rejection_reason}
                          </p>
                        )}
                      </div>
                      
                      {/* Items Preview */}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {order.items?.slice(0, 3).map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {item.item_name}: {item.quantity.toLocaleString()}
                          </Badge>
                        ))}
                        {order.items?.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{order.items.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Quantity Stats */}
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">{order.total_items}</p>
                        <p className="text-xs text-slate-500">Items</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-slate-700">{order.total_quantity?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">Total Qty</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 min-w-[140px]">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setViewingOrder(order); setShowViewDialog(true); }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                      
                      {/* Factory Admin Actions */}
                      {canManageOrders && order.status === 'pending_approval' && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => approveOrder(order.id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                            onClick={() => { setChangingOrder(order); setShowChangesDialog(true); }}
                          >
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Request Changes
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => { setRejectingOrder(order); setShowRejectDialog(true); }}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      
                      {/* Depot Re-upload Action */}
                      {needsReupload && (
                        <Button
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600"
                          onClick={() => { setReuploadingOrder(order); setShowReuploadDialog(true); }}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Re-upload Order
                        </Button>
                      )}
                      
                      {/* Cancel Action */}
                      {((isDepot && ['pending_approval', 'changes_requested', 'rejected'].includes(order.status) && order.created_by === user?.id) ||
                        canManageOrders) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200"
                          onClick={() => cancelOrder(order.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* View Order Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-slate-100 text-slate-700">
                  <Building2 className="w-3 h-3 mr-1" />
                  {viewingOrder.depot_name}
                </Badge>
                <Badge className={`${STATUS_CONFIG[viewingOrder.status]?.color} border-0`}>
                  {STATUS_CONFIG[viewingOrder.status]?.label}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Created</p>
                  <p className="font-medium">{formatDate(viewingOrder.created_at)}</p>
                  <p className="text-slate-500">by {viewingOrder.created_by_name}</p>
                </div>
                {viewingOrder.approved_at && (
                  <div>
                    <p className="text-slate-400">Approved</p>
                    <p className="font-medium">{formatDate(viewingOrder.approved_at)}</p>
                    <p className="text-slate-500">by {viewingOrder.approved_by_name}</p>
                  </div>
                )}
              </div>
              
              {viewingOrder.change_request_reason && (
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-700">
                    <span className="font-medium">Changes Requested:</span> {viewingOrder.change_request_reason}
                  </p>
                </div>
              )}
              
              {viewingOrder.rejection_reason && (
                <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">
                    <span className="font-medium">Rejection reason:</span> {viewingOrder.rejection_reason}
                  </p>
                </div>
              )}
              
              {/* Upload History */}
              {viewingOrder.upload_history && viewingOrder.upload_history.length > 1 && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="font-medium text-sm mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Upload History
                  </p>
                  <div className="space-y-1 text-sm">
                    {viewingOrder.upload_history.map((upload, idx) => (
                      <p key={idx} className="text-slate-600">
                        {formatDate(upload.uploaded_at)} - {upload.total_items} items, {upload.total_quantity.toLocaleString()} qty
                        {upload.reason && <span className="text-slate-400"> ({upload.reason})</span>}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
                <p className="font-medium mb-3">Order Items ({viewingOrder.total_items})</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 font-medium">Item Name</th>
                        <th className="text-left p-3 font-medium">SKU</th>
                        <th className="text-right p-3 font-medium">Current Stock</th>
                        <th className="text-right p-3 font-medium">Buffer</th>
                        <th className="text-right p-3 font-medium">Order Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingOrder.items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-3">{item.item_name}</td>
                          <td className="p-3 text-slate-500">{item.sku}</td>
                          <td className="p-3 text-right">{(item.current_stock || 0).toLocaleString()}</td>
                          <td className="p-3 text-right">{(item.buffer_level || 0).toLocaleString()}</td>
                          <td className="p-3 text-right font-bold text-blue-600">{item.quantity.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-blue-50">
                      <tr>
                        <td colSpan="4" className="p-3 font-bold">Total Quantity</td>
                        <td className="p-3 text-right font-bold text-xl text-blue-600">
                          {viewingOrder.total_quantity?.toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Changes Dialog */}
      <Dialog open={showChangesDialog} onOpenChange={setShowChangesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-orange-600">Request Changes</DialogTitle>
            <DialogDescription>
              The depot will be asked to modify and re-upload the order
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {changingOrder && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{changingOrder.depot_name}</p>
                <p className="text-sm text-slate-500">{changingOrder.total_items} items, {changingOrder.total_quantity?.toLocaleString()} total qty</p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">What changes are needed? *</label>
              <Textarea
                placeholder="Describe the required changes..."
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowChangesDialog(false); setChangingOrder(null); setChangeReason(''); }}>
              Cancel
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600"
              onClick={requestChanges}
              disabled={!changeReason.trim()}
            >
              Request Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Order</DialogTitle>
            <DialogDescription>
              This order will be permanently rejected
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {rejectingOrder && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{rejectingOrder.depot_name}</p>
                <p className="text-sm text-slate-500">{rejectingOrder.total_items} items, {rejectingOrder.total_quantity?.toLocaleString()} total qty</p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Rejection Reason</label>
              <Textarea
                placeholder="Enter reason for rejection (optional)..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectDialog(false); setRejectingOrder(null); setRejectionReason(''); }}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={rejectOrder}
            >
              Reject Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-upload Dialog */}
      <Dialog open={showReuploadDialog} onOpenChange={setShowReuploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-upload Modified Order</DialogTitle>
            <DialogDescription>
              Upload the corrected Excel file with your changes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {reuploadingOrder?.change_request_reason && (
              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                <p className="text-sm font-medium text-orange-700 mb-1">Changes Requested:</p>
                <p className="text-sm text-orange-600">{reuploadingOrder.change_request_reason}</p>
              </div>
            )}
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 mb-4">Upload your modified Excel file</p>
              {uploading ? (
                <div className="max-w-xs mx-auto">
                  <UploadProgress progress={uploadProgress} message="Re-uploading order..." />
                </div>
              ) : (
                <label>
                  <Button 
                    asChild
                    className="bg-orange-500 hover:bg-orange-600 cursor-pointer"
                    disabled={uploading}
                  >
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Select File
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleReupload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReuploadDialog(false); setReuploadingOrder(null); }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkOrders;
